import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles, Typography } from '@material-ui/core';
import { parse } from 'date-fns';
import { useSelector } from 'react-redux';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import CustomDatePicker from '../../shared/customDatePicker';
import CustomRadioGroup from '../../shared/customRadioGroup';
import SelectField from '../../shared/selectField';
import CustomDonutChart from '../../shared/customDonutChart';
import HeadcountCards from '../../shared/headcountCards';
import PageContainer from '../../shared/pageContainer';
import CollapsableFilters from '../../shared/collapsableFilters';
import { APP_PAGES, PARAMS } from '../../../constants/pages';
import {
  API_HEADCOUNT,
  API_HEADCOUNT_AGE,
  API_HEADCOUNT_DEPARTMENTS,
  API_HEADCOUNT_GENDER,
  API_HEADCOUNT_LEVELS,
} from '../../../constants/apiRoutes';
import { sticky } from '../../../constants/helperCssRules';
import { divisionsAndDepartmentsSelector } from '../../../store/selectors/divisionsAndDepartmentsSelector';
import { levelsAndTracksSelector } from '../../../store/selectors/levelsAndTracksSelector';
import { jobTitlesSelector } from '../../../store/selectors/jobTitlesSelector';
import {
  getLevelsAndTracks,
  clearLevelsAndTracks,
} from '../../../store/modules/levelsAndTracks';
import {
  getDivisionsAndDepartments,
  clearDivisionsAndDepartments,
} from '../../../store/modules/divisionsAndDepartments';
import { getJobTitles, clearJobTitles } from '../../../store/modules/jobTitles';
import http from '../../../utility/http';
import { formatDate } from '../../../utility/dateUtils';
import { isArrayEmpty } from '../../../utility/helpers';
import { useTranslations } from '../../../utility/useTranslations';
import {
  buildAgeChartData,
  buildGenderChartData,
  headcountDecorations,
  prepareOptions,
  prepareDepartmentDonutChartData,
  prepareLevelDonutChartData,
  preparePageFilters,
} from './config';
import { parseDuplicateParameters } from '../../../utility/uiUtils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    root: {
      display: 'grid',
      gap: 48,
      gridTemplateColumns: '756px 1fr',
      [breakpoints.up('lg')]: {
        gridTemplateColumns: '756px 1fr',
      },
      [breakpoints.up('xLg')]: {
        gridTemplateColumns: '800px 1fr',
      },
    },
    pageDescription: {
      padding: spacing(8, 0, 6, 0),
      ...sticky(primary.white, 105),
    },
    chartRoot: {
      display: 'grid',
      gap: 53,
      height: 'fit-content',
    },
    filtersWrapper: {
      display: 'grid',
      gridTemplateColumns: '109px 192px 136px',
      gridColumnGap: spacing(3),
      alignItems: 'start',
    },
    filtersWrapperLarge: {
      gridTemplateColumns: '237px 192px 136px',
    },
    chartWrapper: {
      marginTop: spacing(10.5),
      height: 351,
    },
    description: {
      maxWidth: 740,
    },
    subtitle: {
      height: 56,
      paddingBottom: spacing(7),
      marginBottom: spacing(7),
      borderBottom: `1px solid ${primary.bluish7}`,
    },
    datePicker: {
      width: 192,
    },
    chartDescription: {
      paddingTop: spacing(4),
    },
    radioGroup: {
      paddingTop: spacing(9),
      alignItems: 'center',
    },
    filters: {
      paddingTop: spacing(6),
    },
    pieChartHeaderWrapper: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gridColumnGap: spacing(3),
      gap: 16,
      alignItems: 'end',
      maxWidth: 756,
      [breakpoints.up('xLg')]: {
        maxWidth: 800,
      },
    },
    selectWrapper: {
      minWidth: 0,
    },
    pieChartWrapper: {
      alignItems: 'start',
    },
    statsOverviewChart: {
      position: 'relative',
      margin: '0 auto',
      width: '100%',
      maxWidth: 440,
      marginTop: spacing(7),
      marginBottom: 0,
      minHeight: 200,
      [breakpoints.up('xLg')]: {
        marginTop: spacing(7),
        marginBottom: spacing(14),
      },
    },
  })
);

const { JOB_TITLE, TRACK, LEVEL, DIVISION, DEPARTMENT } = PARAMS;

const INITIAL_SELECTED_FILTERS = {
  [JOB_TITLE]: [],
  [TRACK]: [],
  [LEVEL]: [],
  [DIVISION]: [],
  [DEPARTMENT]: [],
};

const CURRENT_DATE = new Date();
const FULL_DATE_FORMAT = 'yyyy-MM-dd';
const MONTH_FORMAT = 'MMM';
const MONTH_YEAR_FORMAT = 'MM-yyyy';

const HeadcountReportPage = ({ dispatch, ...rest }) => {
  const translations = useTranslations(APP_PAGES.HEADCOUNT_REPORT_PAGE);
  const classes = useStyles();
  const [selectedDate, setSelectedDate] = useState(CURRENT_DATE);
  const [headcountData, setHeadcountData] = useState({});
  const [barChartData, setBarChartData] = useState([]);
  const [isGenderOption, setIsGenderOption] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState(
    INITIAL_SELECTED_FILTERS
  );
  const [isLoading, setIsLoading] = useState(true);

  const { tracks } = useSelector(levelsAndTracksSelector);
  const { jobTitles } = useSelector(jobTitlesSelector);
  const { divisions, departments } = useSelector(
    divisionsAndDepartmentsSelector
  );
  const hasSelectedFilters = Object.values(selectedFilters)?.some(
    selectedF => !isArrayEmpty(selectedF)
  );
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [selectedDepartments, setSelectedDepartments] = useState(null);
  const [selectedLevels, setSelectedLevels] = useState(null);

  const handleHeadCountData = useCallback(async () => {
    const chartEndpoint = isGenderOption
      ? API_HEADCOUNT_GENDER
      : API_HEADCOUNT_AGE;
    const { data } = await http.get(chartEndpoint, {
      params: {
        date_to: formatDate(selectedDate, FULL_DATE_FORMAT),
        ...selectedFilters,
      },
      paramsSerializer: d => parseDuplicateParameters(d),
    });
    const updatedData = Object.entries(data)
      .map(([key, value]) => {
        const fullDate = parse(key, MONTH_YEAR_FORMAT, new Date());

        return {
          ...value,
          label: formatDate(fullDate, MONTH_FORMAT),
          date: formatDate(fullDate, FULL_DATE_FORMAT),
          fullDate,
        };
      })
      .sort((a, b) => a.fullDate - b.fullDate)
      .map(({ fullDate, ...restData }) => restData);

    setBarChartData(updatedData);
  }, [isGenderOption, selectedDate, selectedFilters]);

  const getInitialData = useCallback(async () => {
    try {
      await Promise.all([
        getLevelsAndTracks(dispatch),
        getDivisionsAndDepartments(dispatch),
        getJobTitles(dispatch),
      ]);

      const { data } = await http.get(API_HEADCOUNT);
      setHeadcountData(data);
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    handleHeadCountData();
  }, [handleHeadCountData]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  const cleanup = useCallback(() => {
    dispatch(clearJobTitles());
    dispatch(clearLevelsAndTracks());
    dispatch(clearDivisionsAndDepartments());
  }, [dispatch]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const handleSelectDivision = async value => {
    const { data } = await http.get(API_HEADCOUNT_DEPARTMENTS, {
      params: { division: value },
    });
    const division = divisions.find(d => d.id === value);

    setSelectedDepartments(data);
    setSelectedDivision(division);
  };

  const handleSelectTrack = async value => {
    const { data } = await http.get(API_HEADCOUNT_LEVELS, {
      params: { track: value },
    });
    const track = tracks.find(d => d.id === value);

    setSelectedLevels(data);
    setSelectedTrack(track);
  };

  useEffect(() => {
    if (!isArrayEmpty(tracks) && !isArrayEmpty(divisions)) {
      handleSelectDivision(divisions?.[0]?.id);
      handleSelectTrack(tracks?.[0]?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracks, divisions]);

  const handleDateChange = updatedDate => {
    setSelectedDate(updatedDate);
  };

  const collapsableFilters = preparePageFilters(
    translations,
    jobTitles,
    tracks,
    divisions,
    departments
  );

  const handleApplyFilters = updatedFilters => {
    setSelectedFilters(updatedFilters);
  };

  const getDivisionOptions = () => {
    return divisions.reduce((acc, curr) => {
      return isArrayEmpty(curr.departments)
        ? acc
        : [
            ...acc,
            {
              value: curr.id,
              label: curr.name,
            },
          ];
    }, []);
  };

  const getTrackOptions = () => {
    return tracks.reduce((acc, curr) => {
      return isArrayEmpty(curr.levels)
        ? acc
        : [
            ...acc,
            {
              value: curr.id,
              label: curr.name,
            },
          ];
    }, []);
  };

  const chartData = useMemo(
    () =>
      isGenderOption
        ? buildGenderChartData(translations, barChartData)
        : buildAgeChartData(translations, barChartData),
    [barChartData, isGenderOption, translations]
  );

  const renderStatsOverviewSection = (stats, isLevelChart = true) => {
    const data = isLevelChart
      ? prepareLevelDonutChartData(stats, selectedTrack)
      : prepareDepartmentDonutChartData(stats, selectedDivision);

    return (
      <div className={classes.statsOverviewChart}>
        {data && (
          <CustomDonutChart
            data={data}
            placeholderText={translations.overviewPlaceholders.levelOverview}
            hasNotStartedStatus
            isHeadCountReport
          />
        )}
      </div>
    );
  };

  const yMaxValue = useMemo(() => {
    const totals = barChartData.map(item => Number(item.total) || 0);
    const maxValue = Math.max(...totals, 0);
    if (maxValue === 0) return 10;
    const padded = Math.ceil(maxValue * 1.2);
    return Math.max(padded, 10);
  }, [barChartData]);

  return (
    !isLoading && (
      <PageContainer
        {...rest}
        translations={translations}
        dispatch={dispatch}
        shouldPassProps={false}
        isFullWidthContent
      >
        <div className={classes.pageDescription}>
          <Typography variant="body2">{translations.description}</Typography>
        </div>
        <div
          className={classNames(classes.filtersWrapper, {
            [classes.filtersWrapperLarge]: hasSelectedFilters,
          })}
        >
          <CollapsableFilters
            className={classes.filters}
            translations={translations.filters}
            filters={collapsableFilters}
            selectedFilters={selectedFilters}
            onApplyFilters={handleApplyFilters}
            shouldSortAlphabetically
            shouldRemoveFieldSpacing
            shouldRemoveFieldError
          />
          <CustomDatePicker
            label={translations.dateLabel}
            tooltipInfo={translations.dateTooltip}
            className={classes.datePicker}
            value={selectedDate}
            disableToolbar
            onChange={handleDateChange}
            isIconOnStart
          />
          <CustomRadioGroup
            className={classes.radioGroup}
            value={isGenderOption}
            options={[
              {
                value: true,
                label: translations.gender,
              },
              {
                value: false,
                label: translations.age,
              },
            ]}
            onChange={value => setIsGenderOption(value === 'true')}
          />
        </div>
        <div className={classes.root}>
          <div className={classes.chartRoot}>
            <div>
              <div className={classes.chartWrapper}>
                <Bar
                  data={chartData}
                  options={prepareOptions(yMaxValue)}
                  plugins={[headcountDecorations]}
                />
              </div>
              <Typography className={classes.chartDescription} variant="body2">
                {translations.chartDescription}
              </Typography>
            </div>
            <div>
              <div className={classes.pieChartHeaderWrapper}>
                <div className={classes.selectWrapper}>
                  <div className={classes.subtitle}>
                    <Typography variant="h3">
                      {
                        translations.overviewPlaceholders.deparmentOverview
                          .subtitle
                      }
                    </Typography>
                  </div>
                  <SelectField
                    label={translations.divisions}
                    value={selectedDivision?.id || divisions?.[0]?.id}
                    options={getDivisionOptions()}
                    onChange={handleSelectDivision}
                    isSearchDisabled
                    isFullWidth
                  />
                </div>
                <div className={classes.selectWrapper}>
                  <div className={classes.subtitle}>
                    <Typography variant="h3">
                      {translations.overviewPlaceholders.levelOverview.subtitle}
                    </Typography>
                  </div>
                  <SelectField
                    label={translations.tracks}
                    value={selectedTrack?.id || tracks?.[0]?.id}
                    options={getTrackOptions()}
                    onChange={handleSelectTrack}
                    isSearchDisabled
                    isFullWidth
                  />
                </div>
              </div>
              <div
                className={classNames(
                  classes.pieChartHeaderWrapper,
                  classes.pieChartWrapper
                )}
              >
                {renderStatsOverviewSection(selectedDepartments, false)}
                {renderStatsOverviewSection(selectedLevels)}
              </div>
            </div>
          </div>
          <HeadcountCards
            translations={translations}
            headcountData={headcountData}
          />
        </div>
      </PageContainer>
    )
  );
};

HeadcountReportPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
};

export default memo(HeadcountReportPage);
